/** Pure helpers for parent-site hostnames used in embed CSP frame-ancestors. */

export const FRAME_ANCESTOR_HOST_RE =
  /^(\*\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

export const parseFrameAncestorsInput = (raw: string): string[] =>
  raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

/** Strip a pasted scheme/path and return a normalized host token, or null. */
export const normalizeFrameAncestorToken = (raw: string): string | null => {
  let value = raw.trim().toLowerCase();
  if (!value) return null;
  if (value.includes('://')) {
    try {
      value = new URL(value).hostname.toLowerCase();
    } catch {
      return null;
    }
  } else {
    value = value.split('/')[0].split(':')[0];
  }
  if (value.startsWith('*.')) {
    value = `*.${value.slice(2)}`;
  }
  return FRAME_ANCESTOR_HOST_RE.test(value) ? value : null;
};

export const isValidFrameAncestorHost = (raw: string): boolean =>
  normalizeFrameAncestorToken(raw) !== null;

export const buildFrameAncestorsDirective = (
  hosts: readonly string[] | null | undefined,
): string | null => {
  if (!hosts?.length) {
    return null;
  }
  const sources: string[] = ["'self'"];
  const seen = new Set<string>(sources);
  for (const raw of hosts) {
    if (typeof raw !== 'string') {
      continue;
    }
    const host = normalizeFrameAncestorToken(raw);
    if (!host) {
      continue;
    }
    const source = `https://${host}`;
    if (seen.has(source)) {
      continue;
    }
    seen.add(source);
    sources.push(source);
  }
  if (sources.length === 1) {
    return null;
  }
  return sources.join(' ');
};
